/**
 * Caixas de fusíveis — a caixa, seus elementos e o que cada um alimenta.
 *
 * São três níveis, e a hierarquia é o que a tela precisa respeitar:
 *
 *  **caixa** (a caixa física, num modelo)
 *    → **elemento** (cada fusível ou relé dentro dela: F19, F20…)
 *      → **função** (o que aquele elemento alimenta: circuito e pino)
 *
 * **A mesma caixa física existe uma vez por modelo.** No projeto 550 são 120
 * caixas para apenas 7 títulos distintos — é por isso que a listagem traz os
 * modelos, e é por isso que a tela agrupa por título: sem isso são 120 linhas
 * repetidas que ninguém distingue.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "./cliente"

export type CaixaDeFusiveis = {
  id: number
  projetoId: number
  titulo: string
  modo: number
  sistemaId: number | null
  componenteId: number | null
  publicado: boolean
  /** Resolvido pela API na listagem — é o que distingue uma caixa da outra. */
  modelos: number[]
}

/** Um fusível ou relé dentro da caixa. */
export type ElementoDaCaixa = {
  id: number
  caixaFusiveisId: number
  nomeCiclo: string
  nomeFabricante: string
  iconeFabricante: string
  descricao: string
  obs: string
  exibirDescricaoAutomatica: boolean
  exibir: boolean
}

/** O que um elemento alimenta. `origem` é o pinout de onde vem. */
export type FuncaoDoElemento = {
  id: number
  caixaFusiveisTabelaId: number
  funcao: string
  circuito: string
  pino: string
  origem: number
  obs: string
}

export function usarCaixasDoProjeto(projetoId: number) {
  return useQuery({
    queryKey: ["caixas-fusiveis", projetoId] as const,
    enabled: projetoId > 0,
    queryFn: () => obter<CaixaDeFusiveis[]>(`api/caixas-fusiveis/projetos/${projetoId}`, {}),
  })
}

export function usarElementosDaCaixa(caixaId: number) {
  return useQuery({
    queryKey: ["caixa-elementos", caixaId] as const,
    enabled: caixaId > 0,
    queryFn: () => obter<ElementoDaCaixa[]>(`api/caixas-fusiveis/${caixaId}/elementos`, {}),
  })
}

export function usarFuncoesDoElemento(caixaId: number, elementoId: number) {
  return useQuery({
    queryKey: ["caixa-funcoes", caixaId, elementoId] as const,
    enabled: caixaId > 0 && elementoId > 0,
    queryFn: () => obter<FuncaoDoElemento[]>(
      `api/caixas-fusiveis/${caixaId}/elementos/${elementoId}/funcoes`, {}),
  })
}

/**
 * Cria ou altera a caixa, e grava os modelos dela na mesma ida.
 *
 * **São duas chamadas porque a API tem duas rotas**, e no sistema antigo também
 * eram dois passos: o `IncluirFinalizar` grava a caixa e depois insere uma linha
 * em `CaixaFusiveis_modelo` por modelo escolhido.
 *
 * **Sistema e componente são obrigatórios** — a API recusa com 400 e a razão é o
 * dado: das 1.755 caixas de produção, nenhuma está sem um ou sem outro. Na
 * alteração isso importa ainda mais, porque mandar o campo vazio *apagaria* o
 * vínculo que já está lá.
 */
export function usarSalvarCaixa(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, dados, modelos }: {
      id: number | null
      dados: Partial<CaixaDeFusiveis>
      modelos: number[]
    }) => {
      let caixaId = id
      if (id) {
        await api.put(`api/caixas-fusiveis/${id}`, dados, noProjeto(projetoId))
      } else {
        const r = await api.post<CaixaDeFusiveis>(
          "api/caixas-fusiveis", { ...dados, projetoId }, noProjeto(projetoId))
        caixaId = r.data?.id ?? null
      }
      if (caixaId) {
        await api.post(`api/caixas-fusiveis/${caixaId}/modelos`,
          { modeloIds: modelos }, noProjeto(projetoId))
      }
      return caixaId
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["caixas-fusiveis", projetoId] }),
  })
}

/** Exclui a caixa inteira — elementos e funções vão junto, na API. */
export function usarExcluirCaixa(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`api/caixas-fusiveis/${id}`, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["caixas-fusiveis", projetoId] }),
  })
}

export function usarSalvarElemento(projetoId: number, caixaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, dados }: { id: number | null; dados: Partial<ElementoDaCaixa> }) => {
      if (id) await api.put(`api/caixas-fusiveis/${caixaId}/elementos/${id}`, dados, noProjeto(projetoId))
      else await api.post(`api/caixas-fusiveis/${caixaId}/elementos`, dados, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["caixa-elementos", caixaId] }),
  })
}

export function usarExcluirElemento(projetoId: number, caixaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`api/caixas-fusiveis/${caixaId}/elementos/${id}`, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["caixa-elementos", caixaId] }),
  })
}

export function usarSalvarFuncao(projetoId: number, caixaId: number, elementoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, dados }: { id: number | null; dados: Partial<FuncaoDoElemento> }) => {
      const base = `api/caixas-fusiveis/${caixaId}/elementos/${elementoId}/funcoes`
      if (id) await api.put(`${base}/${id}`, dados, noProjeto(projetoId))
      else await api.post(base, dados, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["caixa-funcoes", caixaId, elementoId] }),
  })
}

export function usarExcluirFuncao(projetoId: number, caixaId: number, elementoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(
        `api/caixas-fusiveis/${caixaId}/elementos/${elementoId}/funcoes/${id}`, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["caixa-funcoes", caixaId, elementoId] }),
  })
}

// ------------------------------------------------------------------ regenerar

/**
 * O que a regeneração faria — ou fez.
 *
 * **A conferência é o padrão, e não é zelo à toa.** A regra de fusível do
 * sistema antigo só aceita pino com "F" no nome, e boa parte das caixas tem
 * funções de pinos que não têm: a caixa 142 tem 127 gravadas e a regra produz
 * 19. Aplicar sem olhar empobrece a caixa.
 */
export type Regeneracao = {
  caixaId: number
  titulo: string
  modo: number
  aplicado: boolean
  funcoesQueSaem: number
  tabelasQueFicamVazias: number
  pinosLidos: number
  pinosAproveitados: number
  tabelasQueSeriamCriadas: number
  funcoesQueEntram: number
  avisos: string[]
  amostra: {
    pinoutId: number
    pinoBruto: string
    nome: string
    conector: string
    funcao: string
    circuito: string
  }[]
}

export function usarRegenerarCaixa(projetoId: number, caixaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ aplicar, forcarModoManual }: { aplicar: boolean; forcarModoManual?: boolean }) => {
      const r = await api.post<Regeneracao>(
        `api/caixas-fusiveis/${caixaId}/regenerar?aplicar=${aplicar}` +
          (forcarModoManual ? "&forcarModoManual=true" : ""),
        null,
        noProjeto(projetoId),
      )
      return r.data
    },
    onSuccess: (r) => {
      if (r.aplicado) {
        qc.invalidateQueries({ queryKey: ["caixa-elementos", caixaId] })
        qc.invalidateQueries({ queryKey: ["caixas-fusiveis", projetoId] })
      }
    },
  })
}

// ------------------------------------------------------------------ Excel

export type LinhaDeFusivel = {
  linha: number
  caixaId: number
  nomeCiclo: string
  descricao: string
  situacao: string
}

export type ConferenciaDeFusiveis = {
  projetoId: number
  caixaId: number | null
  linhasNaPlanilha: number
  vaoEntrar: number
  jaExistem: number
  foraDoProjeto: number
  caixaNaoExiste: number
  semDados: number
  criados: number
  aplicado: boolean
  linhas: LinhaDeFusivel[]
}

/**
 * Os dois "Excel de fusíveis" do menu antigo numa chamada. Com `caixaId`, a
 * planilha é de uma caixa e tem duas colunas; sem ele, é o projeto inteiro e
 * traz o id da caixa numa terceira.
 */
export function usarImportarFusiveis(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (p: {
      arquivo: File
      caixaId?: number | null
      colunaCaixa?: number
      colunaNome: number
      colunaDescricao: number
      aplicar: boolean
    }) => {
      const corpo = new FormData()
      corpo.append("arquivo", p.arquivo)
      if (p.caixaId) corpo.append("caixaId", String(p.caixaId))
      if (p.colunaCaixa) corpo.append("colunaCaixa", String(p.colunaCaixa))
      corpo.append("colunaNome", String(p.colunaNome))
      corpo.append("colunaDescricao", String(p.colunaDescricao))
      corpo.append("aplicar", String(p.aplicar))

      const r = await api.post<ConferenciaDeFusiveis>(
        `api/projetos/${projetoId}/importacao-de-fusiveis`, corpo, noProjeto(projetoId),
      )
      return r.data
    },
    onSuccess: (r) => {
      if (r.aplicado) {
        qc.invalidateQueries({ queryKey: ["caixas-fusiveis", projetoId] })
        qc.invalidateQueries({ queryKey: ["caixa-elementos"] })
      }
    },
  })
}

/**
 * Põe a caixa no ar, ou tira.
 *
 * **É rota própria, e não um campo do PUT.** A razão está na API: publicar é o
 * que faz a caixa aparecer para quem paga, e cadastrar sem publicar deixa o
 * trabalho pronto e invisível sem nenhuma tela dizendo isso.
 *
 * **Tirar do ar não apaga nada** — a caixa, seus pinos e suas tabelas continuam;
 * ela só deixa de ser visível para o cliente.
 */
export function usarPublicarCaixa(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, publicar }: { id: number; publicar: boolean }) => {
      await api.post(`api/caixas-fusiveis/${id}/${publicar ? "publicar" : "despublicar"}`,
        null, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["caixas-fusiveis"] }),
  })
}
